import type { AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { axios } from "./axiosFactory";
import { useUser } from "@/composables/user";
import { useAppStore } from "@/composables";

export const TokenHeader = "AuthorizationToken";

const LocaleQueryParamName = "webPrefixCode";
const CurrencyIdQueryParamName = "primaryCurrencyId";

export enum TokenAdd {
  Never,
  IfAvailable,
  Always,
}

export const ApiClient = {
  async get<ResponseDataType>(url: string, params: object = {}): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>("GET", url, params, {}, TokenAdd.Always);
  },

  async getPublic<ResponseDataType>(url: string, params: object = {}, addAuthToken: TokenAdd = TokenAdd.IfAvailable, timeout?: number, signal?: AbortSignal): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest("GET", url, params, {}, addAuthToken, timeout, signal);
  },

  async post<ResponseDataType>(url: string, params: object = {}, data: object = {}, timeout?: number): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest("POST", url, params, data, TokenAdd.Always, timeout);
  },

  async postPublic<ResponseDataType>(url: string, params: object = {}, data: object = {}, addAuthToken: TokenAdd = TokenAdd.IfAvailable, timeout?: number, signal?: AbortSignal): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest("POST", url, params, data, addAuthToken, timeout, signal);
  },

  async put<ResponseDataType>(url: string, params: object = {}, data: object = {}): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest("PUT", url, params, data, TokenAdd.Always);
  },

  async putPublic<ResponseDataType>(url: string, params: object = {}, data: object = {}, addAuthToken: TokenAdd = TokenAdd.IfAvailable): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest("PUT", url, params, data, addAuthToken);
  },

  async delete<ResponseDataType>(url: string, params: object = {}, data: object = {}): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest("DELETE", url, params, data, TokenAdd.Always);
  },

  async deletePublic<ResponseDataType>(url: string, params: object = {}, data: object = {}, addAuthToken: TokenAdd = TokenAdd.IfAvailable): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest("DELETE", url, params, data, addAuthToken);
  },

  async patch<ResponseDataType>(url: string, params: object = {}, data: object = {}): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest("PATCH", url, params, data, TokenAdd.Always);
  },

  async patchPublic<ResponseDataType>(url: string, params: object = {}, data: object = {}, addAuthToken: TokenAdd = TokenAdd.IfAvailable): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest("PATCH", url, params, data, addAuthToken);
  },
};

async function makeRequest<ResponseDataType>(method: Method, url: string, params: object = {}, data: object = {}, addAuthToken: TokenAdd = TokenAdd.Always, timeout?: number, signal?: AbortSignal): Promise<AxiosResponse<ResponseDataType>> {
  const appStore = useAppStore();

  const config = <AxiosRequestConfig>{
    url: url,
    method: method,
    params: {
      ...params,
      [LocaleQueryParamName]: appStore.countryCode.value,
      [CurrencyIdQueryParamName]: appStore.currencyId.value,
    },
    data,
  };

  if (addAuthToken === TokenAdd.Always) {
    const { user } = useUser();
    if (user.value === undefined) {
      throw new Error("Cannot get token when no user is logged in.");
    }
    const token = user.value.token;
    addToken(config, token);
  } else if (addAuthToken === TokenAdd.IfAvailable) {
    const { user } = useUser();
    if (user.value) {
      addToken(config, user.value?.token);
    }
  }

  if (timeout !== undefined) {
    config.timeout = timeout;
  }

  if (signal !== undefined) {
    config.signal = signal;
  }

  return axios.request<ResponseDataType>(config);
}

function addToken(config: AxiosRequestConfig, token: string): void {
  if (typeof config.headers === "undefined") {
    config.headers = {};
  }

  config.headers[TokenHeader] = token;
}
